import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Check, ChevronRight, Plus, Search, WifiOff } from 'lucide-react';
import type { Produtor } from '../schemas';
import { useOnline } from '@/lib/use-online';
import { isNative } from '@/lib/native-auth';
import { useProdutores } from '../api';
import { PropriedadesOfflineCard } from './PropriedadesOfflineCard';

function iniciais(nome: string): string {
  const p = nome.trim().split(/\s+/);
  return ((p[0]?.[0] ?? '') + (p.length > 1 ? (p[p.length - 1]?.[0] ?? '') : '')).toUpperCase();
}
function fmtHa(ha: number | null): string {
  if (ha == null) return '—';
  return `${ha.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} ha`;
}
// Tela 01 · Produtores — entrada do fluxo Produtor → Fazenda → Talhão.
export function ProdutoresView() {
  const navigate = useNavigate();
  const { data: produtores, isLoading, isError } = useProdutores();
  const online = useOnline();
  const [busca, setBusca] = useState('');

  const lista = (produtores ?? []).filter((p) =>
    p.nome.toLowerCase().includes(busca.trim().toLowerCase()),
  );

  // Área total da carteira — soma dos hectares de todos os produtores (não muda
  // com a busca: é o número da carteira, não do filtro).
  const totalHa = useMemo(
    () => (produtores ?? []).reduce((s, p) => s + (p.total_ha ?? 0), 0),
    [produtores],
  );

  return (
    <div className="relative flex h-full flex-col bg-bg">
      <header className="flex items-center justify-between px-4 pt-5 pb-2">
        <h1 className="text-[1.75rem] font-bold tracking-tight text-text">Produtores</h1>
        {!online && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#f7e6c9] px-2.5 py-1 text-[0.72rem] font-medium text-[#6a4a17]">
            <WifiOff size={13} strokeWidth={2} /> Offline
          </span>
        )}
      </header>

      {/* área total da carteira */}
      <div className="px-4 pb-3">
        <div className="flex items-baseline gap-2 rounded-[14px] border border-[var(--agro-card-border)] bg-surface2 px-4 py-3">
          <span className="text-[0.68rem] font-semibold uppercase tracking-wider text-text-muted">Área total</span>
          <span className="ml-auto text-[1.15rem] font-bold tabular-nums text-text">{fmtHa(totalHa)}</span>
          <span className="text-[0.78rem] text-text-2">
            {(produtores ?? []).length} {(produtores ?? []).length === 1 ? 'produtor' : 'produtores'}
          </span>
        </div>
      </div>

      <div className="px-4 pb-2">
        <div className="relative">
          <Search size={16} strokeWidth={1.75} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar produtor"
            className="w-full rounded-full bg-surface2 py-3 pl-10 pr-4 text-[0.875rem] text-text placeholder:text-text-2 focus:outline-none"
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-40">
        {isNative() && <PropriedadesOfflineCard />}

        {isLoading && <p className="py-8 text-center text-sm text-text-muted">Carregando…</p>}
        {isError && (
          <p className="py-8 text-center text-sm text-text-2">
            {online ? 'Não foi possível carregar os produtores.' : 'Sem conexão e sem dados sincronizados. Sincronize com internet.'}
          </p>
        )}
        {!isLoading && !isError && lista.length === 0 && (
          <p className="py-10 text-center text-sm text-text-muted">
            {busca ? 'Nenhum produtor encontrado.' : 'Nenhum produtor com talhões cadastrados.'}
          </p>
        )}

        <div className="flex flex-col gap-2.5">
          {lista.map((p) => (
            <ProdutorRow
              key={p.cpf}
              p={p}
              onClick={() => void navigate({ to: '/agronomia/produtores/$cpf', params: { cpf: p.cpf } })}
            />
          ))}
        </div>
      </div>

      {/* FAB novo produtor */}
      <button
        type="button"
        onClick={() => void navigate({ to: '/agronomia/novo-produtor' })}
        className="absolute bottom-24 right-4 z-10 inline-flex items-center gap-2 rounded-full bg-[var(--accent)] px-5 py-3.5 text-[0.92rem] font-semibold text-[var(--on-solid)] shadow-[var(--shadow-over)]"
      >
        <Plus size={20} strokeWidth={2.25} /> Novo produtor
      </button>
    </div>
  );
}

function ProdutorRow({ p, onClick }: { p: Produtor; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-[12px] border border-[var(--agro-card-border)] bg-surface2 py-2.5 pl-3 pr-3.5 text-left active:scale-[0.99]"
    >
      <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[var(--accent-100)] text-[0.875rem] font-medium text-[var(--accent-900)]">
        {iniciais(p.nome)}
      </span>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-[0.95rem] font-semibold text-text">{p.nome}</span>
        <span className="text-[0.82rem] text-text-2">
          {p.fazendas} {p.fazendas === 1 ? 'fazenda' : 'fazendas'} · {fmtHa(p.total_ha)}
        </span>
      </div>
      <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-[#dcefdf]">
        <Check size={15} strokeWidth={3} className="text-[#1d5a33]" />
      </span>
      <ChevronRight size={18} strokeWidth={2} className="shrink-0 text-[#565350]" />
    </button>
  );
}
